import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { format, parseISO, addDays } from "date-fns";
import { buildPaystackCallbackUrl } from "@/lib/payments";

function money(currency: string, amount: number) {
  return `${currency} ${amount.toFixed(2)}`;
}

export default function MembershipRenewalPage() {
  const { businessSlug, code } = useParams();
  const [loading, setLoading] = useState(true);
  const [membership, setMembership] = useState<any>(null);
  const [payerEmail, setPayerEmail] = useState("");
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const run = async () => {
      if (!businessSlug || !code) {
        setError("Missing business slug or membership code");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const { data, error: rpcError } = await supabase.rpc("get_public_membership", {
          _business_slug: businessSlug,
          _membership_code: code,
        });
        if (rpcError) {
          console.error("RPC error:", rpcError);
          setError(rpcError.message || "Failed to load membership");
          setMembership(null);
        } else {
          const row = (data && data[0]) || null;
          setMembership(row);
          setPayerEmail(String(row?.customer_email || ""));
          if (!row) {
            setError("Membership not found or unavailable.");
          }
        }
      } catch (e: any) {
        console.error("Error loading membership:", e);
        setError(e?.message || "Failed to load membership");
        setMembership(null);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [businessSlug, code]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!membership) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="mx-auto max-w-2xl">
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-2">
                {error || "Membership not found or unavailable."}
              </p>
              {error && (
                <p className="text-xs text-muted-foreground mt-2">
                  Please check the link or contact the business for assistance.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isExpired = membership.expires_at ? parseISO(membership.expires_at) < new Date() : false;
  const isExpiringSoon = membership.expires_at
    ? parseISO(membership.expires_at) <= addDays(new Date(), 7)
    : false;
  const canRenew = (isExpired || isExpiringSoon) && membership.status !== "cancelled";
  const renewalAmount = Number(membership.plan_price_amount || 0);

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="mx-auto max-w-2xl">
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-foreground mb-2">Membership Renewal</h1>
            <p className="text-sm text-muted-foreground">{membership.business_name}</p>
          </div>

          <div className="space-y-4 mb-6">
            <div className="rounded-lg border border-border bg-muted/20 p-4">
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Member</p>
                  <p className="font-semibold text-foreground mt-1">{membership.customer_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Membership Code</p>
                  <p className="font-mono font-semibold text-foreground mt-1">{membership.membership_code}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Plan</p>
                  <p className="font-semibold text-foreground mt-1">{membership.plan_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Status</p>
                  <p className="font-semibold text-foreground mt-1 capitalize">{membership.status}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Expires</p>
                  <p className={`font-semibold mt-1 ${isExpired ? "text-destructive" : isExpiringSoon ? "text-warning" : "text-foreground"}`}>
                    {membership.expires_at ? format(parseISO(membership.expires_at), "MMM d, yyyy") : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Interval</p>
                  <p className="font-semibold text-foreground mt-1 capitalize">{membership.plan_interval}</p>
                </div>
              </div>
            </div>

            {canRenew && renewalAmount > 0 && (
              <div className="rounded-2xl border border-border bg-accent/30 p-4">
                <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                  <div>
                    <p className="text-sm font-semibold text-foreground">Renew Your Membership</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {isExpired
                        ? "Your membership has expired. Renew now to continue enjoying benefits."
                        : "Your membership is expiring soon. Renew now to avoid interruption."}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Renewal Amount</p>
                    <p className="text-lg font-bold text-foreground">{money(String(membership.currency || "GHS"), renewalAmount)}</p>
                    <p className="text-xs text-muted-foreground mt-1">per {membership.plan_interval}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Email for payment receipt</Label>
                    <Input
                      value={payerEmail}
                      onChange={(e) => setPayerEmail(e.target.value)}
                      placeholder="you@example.com"
                      type="email"
                    />
                  </div>
                  <Button
                    className="w-full"
                    disabled={paying || !payerEmail.trim()}
                    onClick={async () => {
                      if (!payerEmail.trim()) return;
                      setPaying(true);
                      try {
                        const callbackUrl = buildPaystackCallbackUrl();
                        const { data, error } = await supabase.functions.invoke("paystack-init", {
                          body: {
                            context: { type: "membership_payment", membershipId: String(membership.membership_id) },
                            payerEmail: payerEmail.trim(),
                            payerName: membership.customer_name || null,
                            callbackUrl,
                          },
                        });
                        if (error) throw error;
                        const url = String((data as any)?.authorizationUrl || "");
                        if (!url) throw new Error("Missing Paystack authorizationUrl");
                        
                        // Safari mobile fix: Use assign() with a small delay to prevent blocking
                        setTimeout(() => {
                          window.location.assign(url);
                        }, 50);
                      } catch (e: any) {
                        alert(e?.message || "Failed to start payment.");
                      } finally {
                        setPaying(false);
                      }
                    }}
                  >
                    {paying ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Redirecting to payment...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Renew Membership
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {!canRenew && (
              <div className="rounded-lg border border-border bg-muted/20 p-4 text-center">
                <p className="text-sm text-muted-foreground">
                  {membership.status === "cancelled"
                    ? "This membership has been cancelled."
                    : "Your membership is still active and does not need renewal at this time."}
                </p>
              </div>
            )}
          </div>

          {membership.business_phone && (
            <div className="mt-6 text-center text-xs text-muted-foreground">
              Questions? Contact {membership.business_name} at {membership.business_phone}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

